import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wifi, Instagram, MapPin, Star, Loader2, CheckCircle, Gift, ExternalLink } from "lucide-react";

// P1-4: Only allow safe absolute http/https URLs — block javascript:, data:, vbscript:, relative paths
function isSafeExternalUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function safeOpen(url) {
  if (!isSafeExternalUrl(url)) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

export default function WiFiPortalForm({ config, onRegister, onAuthorize, onSuccess, hasRedirect }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [instagramClicked, setInstagramClicked] = useState(false);
  const [checkinClicked, setCheckinClicked] = useState(false);
  const [googleClicked, setGoogleClicked] = useState(false);
  const [step, setStep] = useState("form"); // form | success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const primaryColor = config.brand_primary_color || "#FF6B35";

  const handleInstagram = () => { safeOpen(config.instagram_url); setInstagramClicked(true); };
  const handleCheckin = () => { safeOpen(config.checkin_url); setCheckinClicked(true); };
  const handleGoogle = () => { safeOpen(config.google_review_url); setGoogleClicked(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !phone.trim()) {
      setError("Nome e telefone são obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      const data = await onRegister({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        marketing_consent: consent,
        instagram_clicked: instagramClicked,
        checkin_clicked: checkinClicked,
        google_review_clicked: googleClicked,
        user_agent: navigator.userAgent,
      });
      setResult(data);
      // Authorize WiFi — best effort, errors don't block success screen
      if (data?.lead_id) {
        await onAuthorize({ lead_id: data.lead_id }).catch((err) => {
          // Log for debugging but don't surface to guest
          console.warn("[wifi] authorize failed:", err?.response?.data?.detail || err?.message);
        });
      }
      setStep("success");
      onSuccess?.();
    } catch (err) {
      // P2-5: use FastAPI detail message when available
      const detail = err?.response?.data?.detail;
      setError(detail || err?.message || "Erro ao registar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <div className="text-center space-y-4 py-6">
        <CheckCircle className="h-16 w-16 mx-auto" style={{ color: primaryColor }} />
        <h2 className="text-xl font-bold">{config.success_message || "Wi-Fi liberado! Bem-vindo."}</h2>
        {result?.is_returning && (
          <p className="text-sm text-gray-500">Olá de novo! Que bom ter você de volta.</p>
        )}
        {result?.coupon_code && (
          <div className="flex items-center justify-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Gift className="h-5 w-5 text-yellow-600" />
            <span className="font-mono font-bold text-yellow-700">{result.coupon_code}</span>
            <span className="text-sm text-yellow-600">— use no próximo pedido!</span>
          </div>
        )}
        {hasRedirect && (
          <p className="text-xs text-gray-400">A redirecionar para a internet...</p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Social action buttons — only rendered when URL is safe */}
      <div className="space-y-2">
        {isSafeExternalUrl(config.instagram_url) && (
          <button type="button" onClick={handleInstagram}
            className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all ${instagramClicked ? "bg-pink-50 border-pink-400 text-pink-600" : "border-gray-200 text-gray-600 hover:border-pink-300"}`}>
            <Instagram className="h-4 w-4" />
            {instagramClicked ? "Seguindo ✓" : "Seguir no Instagram"}
            {!instagramClicked && <ExternalLink className="h-3 w-3 ml-auto opacity-40" />}
          </button>
        )}
        {isSafeExternalUrl(config.checkin_url) && (
          <button type="button" onClick={handleCheckin}
            className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all ${checkinClicked ? "bg-orange-50 border-orange-400 text-orange-600" : "border-gray-200 text-gray-600 hover:border-orange-300"}`}>
            <MapPin className="h-4 w-4" />
            {checkinClicked ? "Check-in feito ✓" : "Fazer check-in"}
            {!checkinClicked && <ExternalLink className="h-3 w-3 ml-auto opacity-40" />}
          </button>
        )}
        {isSafeExternalUrl(config.google_review_url) && (
          <button type="button" onClick={handleGoogle}
            className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 text-sm font-medium transition-all ${googleClicked ? "bg-yellow-50 border-yellow-400 text-yellow-600" : "border-gray-200 text-gray-600 hover:border-yellow-300"}`}>
            <Star className="h-4 w-4" />
            {googleClicked ? "Avaliado ✓" : "Avaliar no Google"}
            {!googleClicked && <ExternalLink className="h-3 w-3 ml-auto opacity-40" />}
          </button>
        )}
      </div>

      <hr className="border-gray-100" />

      <div className="space-y-3">
        <Input placeholder="Seu nome *" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input placeholder="WhatsApp / Telefone *" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" required />
        <Input placeholder="Email (opcional)" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
      </div>

      {config.marketing_consent_text && (
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded" />
          <span className="text-xs text-gray-500">{config.marketing_consent_text}</span>
        </label>
      )}

      {error && <p className="text-sm text-red-500 text-center">{error}</p>}

      <Button type="submit" disabled={loading} className="w-full py-3 text-base font-semibold text-white" style={{ backgroundColor: primaryColor }}>
        {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Wifi className="h-5 w-5 mr-2" />}
        {loading ? "A libertar acesso..." : "Libertar Wi-Fi agora"}
      </Button>

      {isSafeExternalUrl(config.privacy_policy_url) && (
        <p className="text-center text-xs text-gray-400">
          <a href={config.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="underline">
            Política de privacidade
          </a>
        </p>
      )}
    </form>
  );
}
